import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {ThemeColor} from 'themes'
import {TemplateDropboxHeader, TemplateDropboxMain} from 'pages/templates'
import {Button, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {
  DropboxUploadMapData,
  DropboxUploadPreviewTabType,
  DropboxUploadPreviewTabViewState,
} from 'types/modules'
import {getBorder, useDidMount, useMapRef} from 'utils/commons'
import {useDropboxUploadUseCases} from 'utils/modules'
import {DropboxUploadInfo} from '../UploadInfo'
import {DropboxUploadDropzone} from '../UploadDropzone'
import {DropboxUploadModal} from '../UploadModal'
import {DropboxUploadSuccess} from '../UploadSuccess'
import {DropboxUploadPreview} from '../UploadPreview'
import {DropboxUploadTabView} from '../UploadTabView'

const StyledTextCaution = styled(Paragraph)`
  text-align: center;
  padding: 0 ${convertUnit(12)} ${convertUnit(10)};
  margin-bottom: ${convertUnit(-8)};
  border-bottom: ${({theme}) => getBorder(1, 'solid', theme.gray_1)};
`

export default function DropboxUpload() {
  const mapRef = useMapRef<DropboxUploadMapData>({
    isRunning: false,
    index: -1,
    total: 0,
    totalFailed: 0,
    totalSuccess: 0,
    totalWarning: 0,
    statuses: [],
    listSuccess: [],
    listFailed: [],
    listWarning: [],
    listAllSuccess: [],
  })
  const {registerUseCase, onRetry, onLoadFiles} = useDropboxUploadUseCases(
    mapRef,
  )
  const {map, reset} = mapRef
  const {total, listFailed, listWarning, listAllSuccess} = map.current

  const stateView = useState<DropboxUploadPreviewTabViewState>({
    tab: 'failed',
    visible: false,
  })
  const [tabView, setTabView] = stateView

  const isModalVisible = useMemo(() => !tabView.visible && total > 0, [
    tabView.visible,
    total,
  ])

  const handleRenderNewUpload = useCallback(
    () =>
      tabView.visible ? (
        <Button
          label="+ New Upload"
          onClick={() => {
            reset()
            setTabView(() => ({tab: 'failed', visible: false}))
          }}
        />
      ) : (
        <></>
      ),
    [reset, setTabView, tabView.visible],
  )

  const handleRenderHeader = useCallback(
    () =>
      tabView.visible ? (
        <TemplateDropboxHeader
          title="Uploaded!"
          color="success_6"
          iconType="circular-check"
          iconColor="success_6"
        />
      ) : (
        <TemplateDropboxHeader iconType="upload" title="Upload Content" />
      ),
    [tabView.visible],
  )

  const handleRenderInfo = useCallback(
    () =>
      tabView.visible ? (
        <DropboxUploadSuccess
          total={total}
          totalDuplicates={listWarning.length}
          totalFailed={listFailed.length}
          totalSuccess={listAllSuccess.length}
          renderNewUpload={handleRenderNewUpload}
        />
      ) : (
        <DropboxUploadInfo />
      ),
    [
      handleRenderNewUpload,
      listAllSuccess.length,
      listWarning.length,
      listFailed.length,
      tabView.visible,
      total,
    ],
  )
  const handleRenderContent = useMemo(() => {
    const tabConfig: Record<
      DropboxUploadPreviewTabType,
      {color: ThemeColor; data: File[]; description?: string}
    > = {
      failed: {color: 'danger_5', data: listFailed},
      duplicate: {
        color: 'warning_4',
        data: listWarning,
        description:
          'Duplicate Detected. This file already exists in our database. Uploading the same file from any account is prohibited.',
      },
    }

    const config = tabConfig[tabView.tab as keyof typeof tabConfig]

    return (
      <DropboxUploadPreview
        color={config.color}
        data={config.data}
        renderNewUpload={handleRenderNewUpload}
        renderHeader={
          config?.description
            ? () => (
                <StyledTextCaution color="gray_5">
                  {config?.description}
                </StyledTextCaution>
              )
            : undefined
        }
      />
    )
  }, [handleRenderNewUpload, listWarning, listFailed, tabView.tab])

  const handleRenderDropzone = useCallback(
    () =>
      tabView.visible ? (
        <>
          <DropboxUploadTabView
            stateView={stateView}
            duplicate={listWarning}
            failed={listFailed}
          />
          {handleRenderContent}
        </>
      ) : (
        <DropboxUploadDropzone onLoadFiles={onLoadFiles} />
      ),
    [
      tabView.visible,
      stateView,
      listWarning,
      listFailed,
      handleRenderContent,
      onLoadFiles,
    ],
  )

  useDidMount(registerUseCase)

  return (
    <>
      <DropboxUploadModal
        visible={isModalVisible}
        mapRef={mapRef}
        onClose={reset}
        onRetry={onRetry}
        onViewReport={() =>
          setTabView(() => ({
            tab: listFailed.length > 0 ? 'failed' : 'duplicate',
            visible: true,
          }))
        }
      />
      <TemplateDropboxMain
        renderHeader={handleRenderHeader}
        renderInfo={handleRenderInfo}
        renderContent={handleRenderDropzone}
      />
    </>
  )
}
